import { ElementProps } from '@/components/sections/builder/types';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import { ProductDetailTitleElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Currency } from 'lucide-react';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import { IProduct, ProductItem } from '@/types/product';
import { cn, formatIDR } from '@/lib/utils';
import React from 'react';
import { useProductStore } from '@/stores/product';
import ModalPromotionProduct from '@/components/sections/builder/elements/body/product-detail/components/modal-promotion-product';
import { useGetListProductPromotions } from '@/hooks/api/storefront/promotion';
import { useCartProduct } from '@/stores/cart';
import { useProductDetailPriceBook } from '@/hooks/api/storefront/product-detail';
import Cookies from 'js-cookie';
import PriceBookIcon from '@/components/icons/price-book-icon';
import useModal from '@/hooks/useModal';
import ModalPricebookInformation from '@/components/sections/builder/elements/body/product-detail/components/modal-pricebook-information';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { shallow } from 'zustand/shallow';
import { ElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { toNum } from '@/lib/product';

const defaultData: ProductDetailTitleElementDataType = {
  name: 'Price',
  label: '10000',
};

export const ElementContent = ({ element }: ElementProps) => {
  const modalPricebookInformation = useModal();
  const customerToken = Cookies.get('accessToken') as string;

  const productStore = useProductStore(
    (state) => ({
      price: state.price,
      discountPrice: state.discountPrice,
      isApplicablePricebook: state.isApplicablePricebook,
      setIsApplicablePricebook: state.setIsApplicablePricebook,
    }),
    shallow
  );
  const cartProduct = useCartProduct(
    (state) => ({
      item_id: state.item_id,
      quantity: state.quantity,
    }),
    shallow
  );

  const product = (element.parentData || {}) as IProduct;
  const parentData = element.parentData as ElementDataType & {
    slug: string;
    token: string;
    theme: ThemeType;
  };
  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  const productSlug = parentData.slug;
  const themeContent = parentData?.theme as ThemeType;
  const hasVariants = (product.variants?.length ?? 0) > 0;

  const { data: pricebookData } = useProductDetailPriceBook(productSlug, customerToken as string, {
    enabled: !!productSlug,
  });
  const { data: listPromoProduct } = useGetListProductPromotions(
    {
      slug: String(parentData.slug || ''),
      token: String(parentData.token || ''),
    },
    {
      enabled: !!parentData.slug,
    }
  );

  const productItems = React.useMemo(
    () => (product.product_items || []).filter((pi): pi is ProductItem => !!pi.item_id),
    [product]
  );
  const pricebook = pricebookData || [];
  const cartQty = cartProduct.quantity || 0;
  const isRangeMode = themeContent?.productCard?.priceInformation === 'range';

  const selectedItemId: string | undefined = React.useMemo(() => {
    if (hasVariants) return cartProduct.item_id || undefined;
    return productItems?.[0]?.item_id;
  }, [hasVariants, cartProduct.item_id, productItems]);

  const sellPriceById = React.useMemo(() => {
    const map = new Map<string | number, number>();
    for (const item of productItems) {
      if (item.item_id != null && typeof item.sell_price === 'number') {
        map.set(item.item_id, item.sell_price);
      }
    }
    return map;
  }, [productItems]);

  const getSellPriceForItem = React.useCallback(
    (id?: string | number) => (id != null ? sellPriceById.get(id) ?? 0 : 0),
    [sellPriceById]
  );

  const pureSellPriceSelected = React.useMemo(
    () => getSellPriceForItem(selectedItemId),
    [getSellPriceForItem, selectedItemId]
  );

  const filteredPricebook = React.useMemo(() => {
    if (!selectedItemId) return pricebook;
    return pricebook.filter((item) => item.item_id === selectedItemId);
  }, [pricebook, selectedItemId]);

  const sortedFilteredPricebook = React.useMemo(
    () => [...filteredPricebook].sort((a, b) => Number(a.min_unit) - Number(b.min_unit)),
    [filteredPricebook]
  );

  const top5Pricebook = React.useMemo(() => {
    const seen = new Set<number>();
    const out = [] as typeof sortedFilteredPricebook;
    for (const t of sortedFilteredPricebook) {
      if (seen.has(t.min_unit)) continue;
      seen.add(t.min_unit);
      out.push(t);
      if (out.length === 5) break;
    }
    return out;
  }, [sortedFilteredPricebook]);

  const basePrice = React.useMemo(() => {
    const baseUnitItem = pricebook.find((p) => p.item_id === selectedItemId && p.min_unit === 1);
    if (baseUnitItem && typeof baseUnitItem.retail_price === 'number')
      return baseUnitItem.retail_price;
    return getSellPriceForItem(selectedItemId);
  }, [pricebook, selectedItemId, getSellPriceForItem]);

  const itemsPricing = React.useMemo(() => {
    return (productItems || []).map((pi) => {
      const sell = toNum(pi.sell_price);
      const disc = toNum(pi.discount_price);
      const validDisc = disc > 0 && disc < sell ? disc : 0;
      const effective = validDisc || sell;
      const name = (pi as any).item_name ?? undefined;

      return {
        id: pi.item_id as string | number,
        name,
        sell,
        disc: validDisc,
        effective,
        hasDiscount: validDisc > 0,
      };
    });
  }, [productItems]);

  const { minSell, maxSell, minDisc, maxDisc, discountedVariants } = React.useMemo(() => {
    if (!itemsPricing.length)
      return {
        minSell: 0,
        maxSell: 0,
        minDisc: 0,
        maxDisc: 0,
        discountedVariants: [] as typeof itemsPricing,
      };
    const sells = itemsPricing.map((x) => x.sell);
    const discounted = itemsPricing.filter((x) => x.hasDiscount);
    const discs = discounted.map((x) => x.disc);
    return {
      minSell: Math.min(...sells),
      maxSell: Math.max(...sells),
      minDisc: discs.length ? Math.min(...discs) : 0,
      maxDisc: discs.length ? Math.max(...discs) : 0,
      discountedVariants: discounted,
    };
  }, [itemsPricing]);

  const matchedRetailPrice = React.useMemo(() => {
    if (!selectedItemId) return null;
    let best: number | null = null;
    let bestMin = -1;
    for (const pb of sortedFilteredPricebook) {
      const minUnit = Number(pb.min_unit) || 0; // jaga-jaga kalau string
      if ((cartQty || 0) < minUnit) continue;
      if (minUnit > bestMin && typeof pb.retail_price === 'number') {
        bestMin = minUnit;
        best = pb.retail_price;
      }
    }
    return best;
  }, [cartQty, sortedFilteredPricebook, selectedItemId]);

  React.useEffect(() => {
    if (!selectedItemId) return;
    const applicable = !!matchedRetailPrice && matchedRetailPrice > 0;
    productStore.setIsApplicablePricebook?.(applicable);
  }, [selectedItemId, matchedRetailPrice, productStore.setIsApplicablePricebook]);

  const shouldStrike = (
    finalNum: number,
    baseNum: number,
    source: 'base' | 'disc' | 'pricebook'
  ) => {
    if (source !== 'disc') return false; // hanya disc yang boleh coret
    return finalNum > 0 && baseNum > 0 && finalNum < baseNum;
  };

  const displayBundle = React.useMemo(() => {
    const baseStore = toNum(productStore.price);
    const discStore = toNum(productStore.discountPrice) || 0;
    const pbook = toNum(matchedRetailPrice ?? 0);
    const baseSelected = toNum(pureSellPriceSelected);

    // CASE A — Selected item → trust productStore first (SSR/CSR consistent)
    if (selectedItemId) {
      const base = baseStore || baseSelected || 0;

      let finalVal = base;
      let source: 'base' | 'disc' | 'pricebook' = 'base';

      if (pbook > 0 && pbook < finalVal) {
        finalVal = pbook;
        source = 'pricebook';
      }
      if (discStore > 0 && discStore < finalVal) {
        finalVal = discStore;
        source = 'disc';
      }

      const strike = shouldStrike(finalVal, base, source);

      return {
        finalText: formatIDR(finalVal || base),
        originalText: strike ? formatIDR(base) : null,
        hasStrikethrough: strike,
        displayedItemId: selectedItemId as string | number,
      } as const;
    }

    // CASE B — Range mode + has variants + item present
    if (isRangeMode && hasVariants && itemsPricing.length) {
      // B1 — Exactly one discounted variant → show that disc (with item name), original is sell range
      if (discountedVariants.length === 1) {
        const v = discountedVariants[0];
        const finalNum = toNum(v.disc || v.sell);
        const baseNum = toNum(v.sell);
        const strike = finalNum < baseNum;

        return {
          finalText: `${formatIDR(finalNum)}`,
          originalText: strike
            ? minSell === maxSell
              ? formatIDR(minSell)
              : `${formatIDR(minSell)} - ${formatIDR(maxSell)}`
            : null,
          hasStrikethrough: strike,
          displayedItemId: v.id,
        } as const;
      }

      // B2 — Multiple discounted variants → show discount range
      if (discountedVariants.length > 1) {
        const strike = minDisc > 0 && minSell > 0 && minDisc < minSell;

        return {
          finalText:
            minDisc === maxDisc
              ? formatIDR(minDisc)
              : `${formatIDR(minDisc)} - ${formatIDR(maxDisc)}`,
          originalText: strike
            ? minSell === maxSell
              ? formatIDR(minSell)
              : `${formatIDR(minSell)} - ${formatIDR(maxSell)}`
            : null,
          hasStrikethrough: strike,
          displayedItemId: null,
        } as const;
      }

      // B3 — No discounts -> pure sell range
      return {
        finalText:
          minSell === maxSell
            ? formatIDR(minSell)
            : `${formatIDR(minSell)} - ${formatIDR(maxSell)}`,
        originalText: null,
        hasStrikethrough: false,
        displayedItemId: null,
      } as const;
    }

    // CASE C — Non-range mode: pick cheapest item, show its discount if any
    if (!isRangeMode && itemsPricing.length) {
      const cheapest = itemsPricing.reduce(
        (best, x) => (x.sell < best.sell ? x : best),
        itemsPricing[0]
      );
      const finalNum = cheapest.hasDiscount ? toNum(cheapest.disc) : toNum(cheapest.sell);
      const baseNum = toNum(cheapest.sell);
      const strike = finalNum < baseNum && cheapest.hasDiscount;

      return {
        finalText: formatIDR(finalNum),
        originalText: strike ? formatIDR(baseNum) : null,
        hasStrikethrough: strike,
        displayedItemId: cheapest.id,
      } as const;
    }

    // CASE D — Fallback if no other case matched
    {
      const base = baseStore || 0;
      const finalNum = discStore > 0 && discStore < base ? discStore : base;
      const strike = finalNum < base && discStore > 0;

      return {
        finalText: formatIDR(finalNum || 0),
        originalText: strike ? formatIDR(base) : null,
        hasStrikethrough: strike,
        displayedItemId: selectedItemId ?? null,
      } as const;
    }
  }, [
    selectedItemId,
    isRangeMode,
    hasVariants,
    itemsPricing,
    discountedVariants,
    minSell,
    maxSell,
    minDisc,
    maxDisc,
    productStore.price,
    productStore.discountPrice,
    pureSellPriceSelected,
    matchedRetailPrice,
  ]);

  const finalDisplayPrice = displayBundle.finalText;
  const originalDisplayPrice = displayBundle.originalText;
  const hasStrikeThroughShown = displayBundle.hasStrikethrough;
  const displayedItemId = displayBundle.displayedItemId;

  const showPricebook = React.useMemo(
    () => !!displayedItemId && pricebook.some((pb) => pb.item_id === displayedItemId),
    [displayedItemId, pricebook]
  );

  const getBasePriceForTier = React.useCallback(
    (itemId: number | string | undefined) => {
      if (!itemId) return 0;
      if (selectedItemId) return basePrice;
      return sellPriceById.get(itemId) ?? 0;
    },
    [selectedItemId, basePrice, sellPriceById]
  );

  const top5Derived = React.useMemo(() => {
    const activeTierMinUnit = (() => {
      if (!selectedItemId) return null;
      let best = -1;
      for (const t of sortedFilteredPricebook) {
        const mu = Number(t.min_unit) || 0;
        if (mu <= (cartQty || 0) && mu > best) best = mu;
      }
      return best > -1 ? best : null;
    })();

    return top5Pricebook.map((tier) => {
      const mu = Number(tier.min_unit) || 0;
      const currentBase = getBasePriceForTier(tier.item_id);
      const each = toNum(tier.retail_price);
      const total = each * mu;
      const originalTotal = currentBase * mu;

      return {
        min_unit: mu,
        each,
        baseEach: currentBase,
        total,
        originalTotal,
        saved: Math.max(0, originalTotal - total),
        applied: activeTierMinUnit != null && mu === activeTierMinUnit,
      };
    });
  }, [top5Pricebook, getBasePriceForTier, cartQty, selectedItemId, sortedFilteredPricebook]);

  return (
    <>
      <div data-animation='true' {...getOverrideAnimationParams(data.animation)}>
        <div className={cn(hasStrikeThroughShown && 'flex flex-col items-start')}>
          <span
            className={cn(
              'text_product_primary font-bold',
              data.textStyle === 'body' && 'text-lg md:text-xl lg:text-2xl',
              (data.textStyle === 'subtitle' || !data.textStyle) &&
                'text-base md:text-lg lg:text-xl',
              data.textStyle === 'uppercase' && 'text-base uppercase md:text-lg lg:text-xl'
            )}
          >
            {finalDisplayPrice}
          </span>

          {hasStrikeThroughShown && !!originalDisplayPrice ? (
            <span className='text-base font-normal text-[#C2C2C2] line-through sm:text-base'>
              {originalDisplayPrice}
            </span>
          ) : null}
        </div>

        {listPromoProduct && listPromoProduct.length > 0 ? (
          <div className='mt-2'>
            <ModalPromotionProduct
              listPromotion={listPromoProduct}
              theme={themeContent.promotion?.colorScheme}
            />
          </div>
        ) : null}

        {showPricebook && (
          <div>
            <div className='mb-4 mt-4 flex items-center gap-x-2'>
              <PriceBookIcon className='h-5 w-5 text-black' color='black' />
              <label className='text-sm font-medium '>Tiered Pricing</label>
            </div>

            <div className='mt-2 flex flex-col gap-y-2'>
              {top5Derived.map((pb, idx) => (
                <div key={idx} className='rounded-sm border border-slate-400'>
                  <div className='grid grid-cols-2 p-1'>
                    <div className='flex flex-col p-1'>
                      <h1 className='font-semibold '>Total {formatIDR(pb.total)}</h1>
                      <div className='flex flex-col gap-x-1 lg:flex-row lg:items-center'>
                        {pb.saved > 0 && (
                          <h1 className='font-medium text-red-500 '>Saved {formatIDR(pb.saved)}</h1>
                        )}
                        <h1 className='text-slate-500 line-through'>
                          {formatIDR(pb.originalTotal)}
                        </h1>
                      </div>
                    </div>
                    <div className='p-1 text-right'>
                      <h1 className='font-semibold text-green-500 '>Buy {pb.min_unit} or More</h1>
                      <div className='flex flex-col gap-x-2 lg:flex-row lg:justify-end lg:text-right'>
                        <h1 className='order-1 font-medium md:order-1'>
                          {formatIDR(pb.each)} / Each
                        </h1>
                        <h1 className='order-2 text-slate-500 line-through lg:order-2'>
                          {formatIDR(pb.baseEach)}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {sortedFilteredPricebook.length > 5 && (
              <div className='flex justify-end py-2'>
                <button className='text-xs' onClick={() => modalPricebookInformation.open()}>
                  see more
                </button>
              </div>
            )}
          </div>
        )}

        <ModalPricebookInformation
          openModal={modalPricebookInformation.isShow}
          setOpenModal={modalPricebookInformation.setIsShow}
          pricebookData={pricebook}
          basePrice={basePrice}
          itemId={selectedItemId}
          parentData={product}
          filteredData={sortedFilteredPricebook}
        />
      </div>
    </>
  );
};

export const ElementPreviewIcon = Currency;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  return (
    <React.Fragment>
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle || 'subtitle'}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              textStyle: v,
            })
          }
        />
      </div>
    </React.Fragment>
  );
}

const ProductDetailPrice = {
  type: 'price',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailPrice;
